import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  CheckCircle2, 
  Shield, 
  Lock, 
  Eye, 
  Server, 
  Wifi,
  Globe,
  Building2,
  MessageSquare,
  Clock,
  Fingerprint
} from 'lucide-react';

export const SIMVault = () => {
  const SIMVAULT_LOGO = "https://customer-assets.emergentagent.com/job_flowsecure-fintech/artifacts/xz6y59de_77FA3254-428F-4DC1-8494-71315946C436.png";
  const SIMVAULT_ICON = "https://customer-assets.emergentagent.com/job_flowsecure-fintech/artifacts/xz6y59de_77FA3254-428F-4DC1-8494-71315946C436.png";
  
  const features = [
    {
      icon: <Shield className="w-6 h-6" strokeWidth={1.5} />,
      title: "Secure Facility",
      description: "Your SIM cards are stored in a 24/7 monitored, access-controlled facility."
    },
    {
      icon: <MessageSquare className="w-6 h-6" strokeWidth={1.5} />,
      title: "Message Forwarding",
      description: "All incoming SMS messages are instantly forwarded to your dashboard or email."
    },
    {
      icon: <Globe className="w-6 h-6" strokeWidth={1.5} />,
      title: "Remote Access",
      description: "Monitor your SIM cards from anywhere in the world through our secure platform."
    },
    {
      icon: <Eye className="w-6 h-6" strokeWidth={1.5} />,
      title: "24/7 Monitoring",
      description: "Our team monitors your SIMs around the clock for any suspicious activity."
    },
    {
      icon: <Clock className="w-6 h-6" strokeWidth={1.5} />,
      title: "Real-Time Alerts",
      description: "Instant notifications for all activity on your hosted SIM cards."
    },
    {
      icon: <Fingerprint className="w-6 h-6" strokeWidth={1.5} />,
      title: "Access Control",
      description: "Multi-factor authentication and role-based access for your team."
    }
  ];

  const useCases = [
    {
      icon: <Globe className="w-8 h-8" strokeWidth={1.5} />,
      title: "Diaspora Entrepreneurs",
      description: "Manage your Liberian business accounts securely while living abroad. No need to worry about physical SIM security."
    },
    {
      icon: <Building2 className="w-8 h-8" strokeWidth={1.5} />,
      title: "Multi-Location Businesses",
      description: "Centralize SIM management for businesses with multiple branches or outlets across Liberia."
    },
    {
      icon: <Server className="w-8 h-8" strokeWidth={1.5} />,
      title: "Enterprise Operations",
      description: "Secure SIM hosting for large organizations with high transaction volumes and compliance requirements."
    }
  ];

  const securityFeatures = [
    "Biometric access control",
    "24/7 video surveillance",
    "Climate-controlled environment",
    "Fire suppression systems",
    "Redundant power supply",
    "Encrypted data transmission"
  ];

  return (
    <div data-testid="simvault-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-gradient-to-b from-[#0A1628] to-[#0F1D32] text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-grid opacity-10" />
        
        <div className="container-main relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-6">
                <img 
                  src={SIMVAULT_LOGO} 
                  alt="SIMVault" 
                  className="h-16 sm:h-20 object-contain"
                  data-testid="simvault-logo"
                />
              </div>
              
              <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed">
                Enterprise-grade SIM hosting infrastructure. Securely store and monitor your 
                mobile money SIMs from anywhere in the world.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link to="/contact?type=demo">
                  <Button className="btn-accent" data-testid="simvault-demo-btn">
                    Request Demo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10 rounded-full px-8 py-3" data-testid="simvault-contact-btn">
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="bg-[#0F1D32]/50 backdrop-blur rounded-2xl border border-[#1E3A5F] p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <div className="font-semibold text-white">SIM Status</div>
                    <div className="text-sm text-emerald-400 flex items-center gap-1">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                      All systems secure
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {[
                    { label: "Hosted SIMs", value: "12", status: "Active" },
                    { label: "Messages Today", value: "47", status: "Forwarded" },
                    { label: "Last Check", value: "2 min ago", status: "Monitoring" },
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-[#1E3A5F]/50 rounded-lg">
                      <div>
                        <div className="text-sm text-slate-400">{item.label}</div>
                        <div className="text-xl font-semibold text-white">{item.value}</div>
                      </div>
                      <span className="text-xs text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full">
                        {item.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section-padding bg-white" data-testid="simvault-problem">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block">
              The Problem
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Physical SIMs Are a Security Risk
            </h2>
            <p className="text-slate-600 text-lg">
              Business owners, especially those abroad, face significant challenges keeping their 
              mobile money SIM cards secure. Lost, stolen, or compromised SIMs can mean losing 
              access to your business funds.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-red-50 mx-auto mb-4 flex items-center justify-center">
                <Lock className="w-8 h-8 text-red-500" strokeWidth={1.5} />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Theft Risk</h3>
              <p className="text-slate-600 text-sm">Physical SIMs can be stolen, giving thieves access to your accounts.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-amber-50 mx-auto mb-4 flex items-center justify-center">
                <Eye className="w-8 h-8 text-amber-500" strokeWidth={1.5} />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">No Visibility</h3>
              <p className="text-slate-600 text-sm">When abroad, you can't see what's happening on your SIM in real-time.</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 rounded-full bg-slate-100 mx-auto mb-4 flex items-center justify-center">
                <Wifi className="w-8 h-8 text-slate-500" strokeWidth={1.5} />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Connectivity Issues</h3>
              <p className="text-slate-600 text-sm">SIMs left in phones can lose signal or run out of battery.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-slate-50" data-testid="simvault-features">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block">
              Features
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Enterprise-Grade Security
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              SIMVault provides the infrastructure and security your SIM cards need.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="feature-card"
                data-testid={`simvault-feature-${index}`}
              >
                <div className="icon-container mb-6 bg-slate-100 text-slate-700">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="section-padding bg-white" data-testid="simvault-usecases">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-blue-600 uppercase tracking-widest mb-4 block">
              Use Cases
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Who Uses SIMVault?
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="bg-slate-50 rounded-3xl p-8 border border-slate-100"
                data-testid={`simvault-usecase-${index}`}
              >
                <div className="icon-container-lg mb-6 bg-blue-50 text-blue-600">
                  {useCase.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {useCase.title}
                </h3>
                <p className="text-slate-600">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="section-padding bg-[#0A1628] text-white" data-testid="simvault-security">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-medium text-blue-400 uppercase tracking-widest mb-4 block">
                Security
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold mb-6">
                Bank-Level Security for Your SIMs
              </h2>
              <p className="text-slate-300 mb-8 leading-relaxed">
                Our facility is designed with multiple layers of security to ensure your 
                SIM cards and the data they receive are protected at all times.
              </p>
              
              <ul className="grid grid-cols-2 gap-4">
                {securityFeatures.map((feature, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-[#0077B6]/20 rounded-3xl blur-3xl" />
              <div className="relative bg-[#0F1D32] rounded-3xl p-8 border border-[#1E3A5F]">
                <div className="text-center">
                  <Shield className="w-20 h-20 mx-auto text-[#00B4D8] mb-6" strokeWidth={1} />
                  <div className="text-5xl font-bold mb-2">99.99%</div>
                  <div className="text-slate-400">Security Rating</div>
                </div>
                
                <div className="mt-8 pt-8 border-t border-[#1E3A5F] grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold">0</div>
                    <div className="text-sm text-slate-400">Security Incidents</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">24/7</div>
                    <div className="text-sm text-slate-400">Monitoring</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-[#0F1D32] to-[#0A1628]" data-testid="simvault-cta">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
              Secure Your SIMs Today
            </h2>
            <p className="text-slate-300 text-lg mb-10 leading-relaxed">
              Join business owners who have moved their SIMs to a secure, monitored facility.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=demo">
                <Button className="btn-accent" data-testid="simvault-cta-demo">
                  Request Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10 rounded-full px-8 py-6" data-testid="simvault-cta-contact">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SIMVault;
